/**
 * Hooks TanStack Query pour les Templates documents
 * Pattern: Component → Hook → Service
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentTemplatesAPI } from '../services/templates.service';
import type { DocumentTemplate, CurrentTemplates } from '../services/templates.service';
import { notify } from '@/stores/notificationStore';
import { logger } from '@/utils/logger';

// ==================== Query Keys ====================

export const templateKeys = {
  all: ['templates'] as const,
  available: () => [...templateKeys.all, 'available'] as const,
  current: () => [...templateKeys.all, 'current'] as const,
};

// ==================== Queries ====================

/**
 * Hook pour récupérer les templates disponibles
 */
export function useAvailableTemplates() {
  return useQuery<DocumentTemplate[]>({
    queryKey: templateKeys.available(),
    queryFn: () => documentTemplatesAPI.getAvailable(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook pour récupérer les templates actuellement sélectionnés
 */
export function useCurrentTemplates() {
  return useQuery<CurrentTemplates | null>({
    queryKey: templateKeys.current(),
    queryFn: () => documentTemplatesAPI.getCurrent(),
    staleTime: 2 * 60 * 1000,
  });
}

// ==================== Mutations ====================

/**
 * Hook pour sauvegarder les préférences de templates
 */
export function useSaveTemplatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { invoiceTemplateId: string; quoteTemplateId: string }) =>
      documentTemplatesAPI.savePreferences(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.current() });
      notify.success('Templates mis à jour avec succès !');
    },
    onError: (error: Error) => {
      logger.error('Erreur sauvegarde templates:', error);
      notify.error(error.message || 'Erreur lors de la sauvegarde des templates');
    },
  });
}
